import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { stripeAPI } from '../services/api';
import {
  CreditCard,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  MessageSquare,
  Bot,
  BarChart2,
  LifeBuoy,
  ChevronRight,
  Zap,
  Clock,
  Send
} from 'lucide-react';
import axios from 'axios';

// ─── Limiti per piano ────────────────────────────────────────────────────────
const PLAN_LIMITS = {
  Starter:      { agents: 1, conversations: 500 },
  Professional: { agents: 3, conversations: 2000 },
  Enterprise:   { agents: 5, conversations: null },
};

// ─── Dati mock agenti (in futuro da API) ────────────────────────────────────
const MOCK_AGENTS = [
  { id: 1, name: 'Assistente Negozio', channel: 'WhatsApp', status: 'active', conversations_month: 142 },
];

const TICKET_STATUS = {
  open:        { label: 'Aperto',      color: 'var(--warning)' },
  in_progress: { label: 'In gestione', color: 'var(--primary)' },
  closed:      { label: 'Chiuso',      color: 'var(--success)' },
};

const Dashboard = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage]           = useState(null);

  // Ticket state
  const [ticketOpen, setTicketOpen]       = useState(false);
  const [ticket, setTicket]               = useState({ subject: '', body: '', agentId: '' });
  const [ticketSending, setTicketSending] = useState(false);
  const [ticketSent, setTicketSent]       = useState(false);
  const [myTickets, setMyTickets]         = useState([]);

  useEffect(() => {
    loadSubscription();
    loadMyTickets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubscription = async () => {
    try {
      const response = await stripeAPI.getSubscription();
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error('Errore caricamento abbonamento:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyTickets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/tickets`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMyTickets(res.data.tickets || []);
    } catch (e) {
      console.error('Errore caricamento ticket:', e);
    }
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await stripeAPI.createPortalSession();
      window.location.href = response.data.url;
    } catch (error) {
      setMessage({ type: 'error', text: "Errore durante l'apertura del portale clienti" });
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare l\'abbonamento? Rimarrà attivo fino alla fine del periodo corrente.')) return;
    setActionLoading(true);
    try {
      await stripeAPI.cancelSubscription();
      setMessage({ type: 'success', text: 'Abbonamento cancellato. Rimarrà attivo fino alla fine del periodo.' });
      await loadSubscription();
    } catch (error) {
      setMessage({ type: 'error', text: "Errore durante la cancellazione dell'abbonamento" });
    } finally { setActionLoading(false); }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    try {
      await stripeAPI.reactivateSubscription();
      setMessage({ type: 'success', text: 'Abbonamento riattivato con successo!' });
      await loadSubscription();
    } catch (error) {
      setMessage({ type: 'error', text: "Errore durante la riattivazione dell'abbonamento" });
    } finally { setActionLoading(false); }
  };

  const handleSendTicket = async () => {
    if (!ticket.subject.trim() || !ticket.body.trim()) return;
    setTicketSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/tickets`, {
        subject: ticket.subject,
        body: ticket.body,
        agent_name: ticket.agentId ? MOCK_AGENTS.find(a => a.id == ticket.agentId)?.name : null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTicketSent(true);
      setTicket({ subject: '', body: '', agentId: '' });
      await loadMyTickets(); // ricarica lista ticket
      setTimeout(() => { setTicketOpen(false); setTicketSent(false); }, 3000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Errore invio ticket.' });
    } finally { setTicketSending(false); }
  };

  const getStatusBadge = (status) => {
    const map = {
      active:     { cls: 'badge-success', text: 'Attivo',           icon: <CheckCircle size={14}/> },
      trialing:   { cls: 'badge-info',    text: 'Periodo di Prova',  icon: <CheckCircle size={14}/> },
      past_due:   { cls: 'badge-warning', text: 'Pagamento Scaduto', icon: <AlertCircle size={14}/> },
      canceled:   { cls: 'badge-danger',  text: 'Cancellato',        icon: <XCircle size={14}/> },
      incomplete: { cls: 'badge-warning', text: 'Incompleto',        icon: <AlertCircle size={14}/> },
    };
    const b = map[status] || { cls: 'badge-info', text: status, icon: null };
    return <span className={`badge ${b.cls}`} style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>{b.icon}{b.text}</span>;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const formatDateShort = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const planName     = subscription?.plan_name || '';
  const limits       = PLAN_LIMITS[planName] || null;
  const usedConv     = MOCK_AGENTS.reduce((s, a) => s + a.conversations_month, 0);
  const convPct      = limits && limits.conversations ? Math.min(100, Math.round(usedConv / limits.conversations * 100)) : 0;
  const activeAgents = MOCK_AGENTS.filter(a => a.status === 'active').length;

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Caricamento dashboard…</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">

        {/* ── Header ── */}
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p style={{ color: 'var(--gray)', marginTop: '0.5rem' }}>
            Benvenuto, <strong>{user.name}</strong>
          </p>
        </div>

        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'}`} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            {message.type === 'error' ? <AlertCircle size={20}/> : <CheckCircle size={20}/>}
            {message.text}
          </div>
        )}

        {/* ── Riga 1: Account + Abbonamento ── */}
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="card-header"><h3 className="card-title">Il Mio Account</h3></div>
            <div className="card-content">
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Nome:</strong> {user.name}</p>
              {user.company && <p><strong>Azienda:</strong> {user.company}</p>}
              {user.phone   && <p><strong>Telefono:</strong> {user.phone}</p>}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title">
                <CreditCard size={20} style={{ marginRight:'0.5rem' }}/>Abbonamento
              </h3>
            </div>
            <div className="card-content">
              {subscription ? (
                <>
                  <p style={{ marginBottom:'0.5rem' }}><strong>Piano:</strong> {subscription.plan_name}</p>
                  <p style={{ marginBottom:'0.5rem' }}><strong>Stato:</strong> {getStatusBadge(subscription.status)}</p>
                  {subscription.cancel_at_period_end ? (
                    <div className="alert alert-warning" style={{ padding:'0.75rem', marginTop:'1rem', display:'flex', alignItems:'center', gap:'8px' }}>
                      <AlertCircle size={18}/>
                      Termina il {formatDate(subscription.current_period_end)}
                    </div>
                  ) : (
                    <p style={{ color:'var(--gray)', marginTop:'0.5rem', display:'flex', alignItems:'center', gap:'6px' }}>
                      <Calendar size={16}/>
                      Rinnovo: {formatDate(subscription.current_period_end)}
                    </p>
                  )}
                  <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginTop:'1.25rem' }}>
                    <button onClick={handleManageSubscription} disabled={actionLoading} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <ExternalLink size={16}/> Gestisci Pagamenti
                    </button>
                    {subscription.cancel_at_period_end ? (
                      <button onClick={handleReactivateSubscription} disabled={actionLoading} className="btn btn-success" style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <RefreshCw size={16}/> Riattiva
                      </button>
                    ) : (
                      subscription.status === 'active' && (
                        <button onClick={handleCancelSubscription} disabled={actionLoading} className="btn btn-danger" style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                          <XCircle size={16}/> Cancella
                        </button>
                      )
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <p style={{ color:'var(--gray)', marginBottom:'1rem' }}>Nessun abbonamento attivo.</p>
                  <Link to="/pricing" className="btn btn-primary">Visualizza Piani</Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Riga 2: Statistiche utilizzo ── */}
        {subscription && limits && (
          <div className="dashboard-card" style={{ marginTop:'2rem' }}>
            <div className="card-header">
              <h3 className="card-title"><BarChart2 size={20} style={{ marginRight:'0.5rem' }}/>Utilizzo questo mese</h3>
            </div>
            <div className="card-content">
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'1.5rem' }}>
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.4rem' }}>
                    <span style={{ fontSize:'0.875rem', color:'var(--gray)' }}>Conversazioni</span>
                    <span style={{ fontSize:'0.875rem', fontWeight:600 }}>{usedConv} / {limits.conversations ?? '∞'}</span>
                  </div>
                  {limits.conversations && (
                    <div style={{ background:'var(--light-gray)', borderRadius:'99px', height:'8px' }}>
                      <div style={{ width:`${convPct}%`, height:'100%', borderRadius:'99px', background: convPct > 85 ? 'var(--danger)' : 'var(--primary)', transition:'width 0.4s ease' }}/>
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                  <span style={{ fontSize:'0.875rem', color:'var(--gray)' }}>Agenti attivi</span>
                  <span style={{ fontSize:'1.5rem', fontWeight:700, color:'var(--primary)' }}>
                    {activeAgents} <span style={{ fontSize:'0.875rem', fontWeight:400, color:'var(--gray)' }}>/ {limits.agents}</span>
                  </span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                  <span style={{ fontSize:'0.875rem', color:'var(--gray)' }}>Messaggi totali</span>
                  <span style={{ fontSize:'1.5rem', fontWeight:700, color:'var(--gray)' }}>—</span>
                  <span style={{ fontSize:'0.75rem', color:'var(--gray)' }}>Disponibile con Chatwoot</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Riga 3: Agenti ── */}
        <div className="dashboard-card" style={{ marginTop:'2rem' }}>
          <div className="card-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 className="card-title"><Bot size={20} style={{ marginRight:'0.5rem' }}/>I Tuoi Agenti AI</h3>
            {subscription && limits && activeAgents < limits.agents && (
              <span style={{ fontSize:'0.8rem', color:'var(--primary)', display:'flex', alignItems:'center', gap:'4px' }}>
                <Zap size={14}/> {limits.agents - activeAgents} slot disponibili
              </span>
            )}
          </div>
          <div className="card-content">
            {!subscription ? (
              <p style={{ color:'var(--gray)' }}>Attiva un piano per configurare i tuoi agenti.</p>
            ) : MOCK_AGENTS.length === 0 ? (
              <div style={{ textAlign:'center', padding:'2rem 0' }}>
                <Bot size={40} style={{ color:'var(--gray)', marginBottom:'1rem' }}/>
                <p style={{ color:'var(--gray)', marginBottom:'1rem' }}>Nessun agente configurato.</p>
                <button className="btn btn-primary">Richiedi configurazione agente</button>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {MOCK_AGENTS.map(agent => (
                  <div key={agent.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem', background:'var(--light-gray)', borderRadius:'8px', flexWrap:'wrap', gap:'0.75rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                      <div style={{ width:'10px', height:'10px', borderRadius:'50%', background: agent.status === 'active' ? '#22c55e' : '#94a3b8', flexShrink:0 }}/>
                      <div>
                        <p style={{ fontWeight:600, marginBottom:'2px' }}>{agent.name}</p>
                        <p style={{ fontSize:'0.8rem', color:'var(--gray)', display:'flex', alignItems:'center', gap:'4px' }}>
                          <MessageSquare size={12}/> {agent.channel}
                          <span style={{ margin:'0 4px' }}>·</span>
                          <Clock size={12}/> {agent.conversations_month} conv. questo mese
                        </p>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                      <button className="btn btn-secondary" style={{ fontSize:'0.8rem', padding:'0.4rem 0.75rem', display:'flex', alignItems:'center', gap:'4px', opacity:0.5, cursor:'not-allowed' }} disabled>
                        <ExternalLink size={13}/> Chatwoot
                      </button>
                      <button className="btn btn-primary" style={{ fontSize:'0.8rem', padding:'0.4rem 0.75rem', display:'flex', alignItems:'center', gap:'4px' }}>
                        Configura <ChevronRight size={13}/>
                      </button>
                    </div>
                  </div>
                ))}
                {limits && Array.from({ length: limits.agents - MOCK_AGENTS.length }).map((_, i) => (
                  <div key={`empty-${i}`} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem', border:'2px dashed var(--light-gray)', borderRadius:'8px', flexWrap:'wrap', gap:'0.75rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                      <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#e2e8f0', flexShrink:0 }}/>
                      <p style={{ color:'var(--gray)', fontSize:'0.875rem' }}>Slot agente disponibile</p>
                    </div>
                    <button className="btn btn-primary" style={{ fontSize:'0.8rem', padding:'0.4rem 0.75rem' }}>Richiedi agente</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Riga 4: Supporto ── */}
        <div className="dashboard-card" style={{ marginTop:'2rem' }}>
          <div className="card-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 className="card-title"><LifeBuoy size={20} style={{ marginRight:'0.5rem' }}/>Supporto</h3>
            {!ticketOpen && (
              <button onClick={() => setTicketOpen(true)} className="btn btn-primary" style={{ fontSize:'0.8rem', padding:'0.4rem 0.75rem', display:'flex', alignItems:'center', gap:'4px' }}>
                <Send size={13}/> Apri ticket
              </button>
            )}
          </div>
          <div className="card-content">

            {/* Form ticket */}
            {!ticketOpen ? (
              <p style={{ color:'var(--gray)', fontSize:'0.875rem' }}>
                Hai bisogno di aiuto? Apri un ticket e ti risponderemo entro 24 ore.
              </p>
            ) : ticketSent ? (
              <div style={{ display:'flex', alignItems:'center', gap:'8px', color:'var(--success)' }}>
                <CheckCircle size={20}/> Ticket inviato! Ti risponderemo presto.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', maxWidth:'560px' }}>
                {MOCK_AGENTS.length > 0 && (
                  <select value={ticket.agentId} onChange={e => setTicket(t => ({ ...t, agentId: e.target.value }))} style={{ padding:'0.5rem 0.75rem', borderRadius:'6px', border:'1px solid var(--light-gray)', fontSize:'0.875rem' }}>
                    <option value="">Agente coinvolto (opzionale)</option>
                    {MOCK_AGENTS.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                )}
                <input type="text" placeholder="Oggetto" value={ticket.subject} onChange={e => setTicket(t => ({ ...t, subject: e.target.value }))} style={{ padding:'0.5rem 0.75rem', borderRadius:'6px', border:'1px solid var(--light-gray)', fontSize:'0.875rem' }}/>
                <textarea placeholder="Descrivi il problema…" value={ticket.body} onChange={e => setTicket(t => ({ ...t, body: e.target.value }))} rows={4} style={{ padding:'0.5rem 0.75rem', borderRadius:'6px', border:'1px solid var(--light-gray)', fontSize:'0.875rem', resize:'vertical' }}/>
                <div style={{ display:'flex', gap:'0.75rem' }}>
                  <button onClick={handleSendTicket} disabled={ticketSending || !ticket.subject.trim() || !ticket.body.trim()} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    {ticketSending ? <RefreshCw size={14} style={{ animation:'spin 1s linear infinite' }}/> : <Send size={14}/>}
                    {ticketSending ? 'Invio…' : 'Invia ticket'}
                  </button>
                  <button onClick={() => setTicketOpen(false)} className="btn btn-secondary">Annulla</button>
                </div>
              </div>
            )}

            {/* Lista ticket inviati */}
            {myTickets.length > 0 && (
              <div style={{ marginTop:'1.5rem' }}>
                <p style={{ fontWeight:600, marginBottom:'0.75rem', fontSize:'0.875rem', color:'var(--gray)' }}>
                  I tuoi ticket ({myTickets.length}):
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {myTickets.map(t => {
                    const s = TICKET_STATUS[t.status] || { label: t.status, color:'var(--gray)' };
                    return (
                      <div key={t.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.75rem 1rem', background:'var(--light-gray)', borderRadius:'6px', flexWrap:'wrap', gap:'0.5rem' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                          <span style={{ fontSize:'0.75rem', color:'var(--gray)', fontWeight:600 }}>#{t.id}</span>
                          <span style={{ fontSize:'0.875rem' }}>{t.subject}</span>
                          {t.agent_name && <span style={{ fontSize:'0.75rem', color:'var(--gray)' }}>· {t.agent_name}</span>}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                          <span style={{ fontSize:'0.75rem', color:'var(--gray)' }}>{formatDateShort(t.created_at)}</span>
                          <span style={{ fontSize:'0.75rem', fontWeight:700, color: s.color }}>{s.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
